import { Injectable, signal, computed } from '@angular/core';
import { DrawerConfig } from '@sbd-account-portal/app/modules/main-fuse-drawer/menus-buttons-template.types';
import { FuseDrawerMode, FuseDrawerPosition } from '@fuse/components/drawer';
import { LoadedQuoteRequestsItem } from '@sbd-doug-frontend/app/modules/quotes/request/request.types';

export type DrawerContentType = 'loaded-quote-request' | 'rider';
export type DrawerMode = 'over' | 'push' | 'side';


export interface DrawerContent {
  type: DrawerContentType;
  component?: string;
  data?: any;
  config?: DrawerConfig;
}

@Injectable({
  providedIn: 'root',
})
export class MainDrawerService {
  // Core drawer state signals
  private _isOpen = signal<boolean>(false);
  private _content = signal<DrawerContent | null>(null);
  private _loading = signal<boolean>(false);
  private _title = signal<string>('');
  private _subtitle = signal<string>('');
  private _showCloseButton = signal<boolean>(true);
  private _allowOutsideClick = signal<boolean>(true);
  private _disableOverlayClick = signal<boolean>(false);
  private _width = signal<string>('400px');
  private _position = signal<FuseDrawerPosition>('right');
  private _mode = signal<FuseDrawerMode>('over');
  private _hasBackdrop = signal<boolean>(true);
  private _disableClose = signal<boolean>(false);
  private _sizeType = signal<'small' | 'half' | 'large'>('half');
  quoteSelectionChanged = signal<LoadedQuoteRequestsItem>(null);
  mostRecentSelectedQuoteId = signal<number>(-1);


  // Configuration signals
  private _config = signal<DrawerConfig | null>(null);
  private _context = signal<any>(null);

  // Public readonly signals
  readonly isOpen = this._isOpen.asReadonly();
  readonly content = this._content.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly title = this._title.asReadonly();
  readonly subtitle = this._subtitle.asReadonly();
  readonly mode = this._mode.asReadonly();
  readonly showCloseButton = this._showCloseButton.asReadonly();
  readonly allowOutsideClick = this._allowOutsideClick.asReadonly();
  readonly disableOverlayClick = this._disableOverlayClick.asReadonly();
  readonly width = this._width.asReadonly();
  readonly position = this._position.asReadonly();
  readonly config = this._config.asReadonly();
  readonly hasBackdrop = this._hasBackdrop.asReadonly();
  readonly disableClose = this._disableClose.asReadonly();
  readonly context = this._context.asReadonly();
  readonly sizeType = this._sizeType.asReadonly();

  // Computed signals
  readonly hasContent = computed(() => this.content() !== null);
  readonly canClose = computed(() => !this.loading() && this.showCloseButton());
  readonly drawerClasses = computed(() => {
    const classes = ['main-drawer'];
    if (this.loading()) classes.push('loading');
    if (this.content()?.type) classes.push(`content-${this.content()?.type}`);
    return classes.join(' ');
  });

  /**
   * Open the drawer with specific content
   */
  open(content: DrawerContent, options?: {
    title?: string;
    subtitle?: string;
    width?: string;
    position?: FuseDrawerPosition;
    showCloseButton?: boolean;
    allowOutsideClick?: boolean;
    disableOverlayClick?: boolean;
  }): void {
    this._content.set(content);
    this._config.set(content.config || null);
    this._context.set(content.data || null);

    if (options) {
      if (options.title !== undefined) this._title.set(options.title);
      if (options.subtitle !== undefined) this._subtitle.set(options.subtitle);
      if (options.width !== undefined) this._width.set(options.width);
      if (options.position !== undefined) this._position.set(options.position);
      if (options.showCloseButton !== undefined) this._showCloseButton.set(options.showCloseButton);
      if (options.allowOutsideClick !== undefined) this._allowOutsideClick.set(options.allowOutsideClick);
      if (options.disableOverlayClick !== undefined) this._disableOverlayClick.set(options.disableOverlayClick);
    }

    console.log('Drawer opened:', this.getState());
    this._isOpen.set(true);
  }

  /**
   * Toggle drawer state
   */
  toggle(): void {
    if (this.isOpen()) {
      this.onDrawerClosed();
    } else {
      // Note: toggle without content will just open empty drawer
      this._isOpen.set(true);
    }
  }

  /**
   * Update the drawer content without closing/opening
   */
  updateContent(content: DrawerContent): void {
    this._content.set(content);
    this._config.set(content.config || null);
    this._context.set(content.data || null);
  }

  /**
   * Update the drawer configuration
   */
  updateConfig(config: Partial<DrawerConfig>): void {
    const currentConfig = this._config() || {};
    this._config.set({ ...currentConfig, ...config });
  }

  /**
   * Update the drawer context/data
   */
  updateContext(context: any): void {
    this._context.set(context);
  }

  /**
   * Set loading state
   */
  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  /**
   * Update drawer title
   */
  setTitle(title: string): void {
    this._title.set(title);
  }

  /**
   * Update drawer subtitle
   */
  setSubtitle(subtitle: string): void {
    this._subtitle.set(subtitle);
  }

  /**
   * Update drawer width
   */
  setWidth(width: string): void {
    this._width.set(width);
  }

  /**
   * Update drawer position
   */
  setPosition(position: FuseDrawerPosition): void {
    this._position.set(position);
  }

  /**
   * Reset all options to defaults
   */
  private resetToDefaults(): void {
    this._width.set('400px');
    this._position.set('left');
    this._showCloseButton.set(true);
    this._allowOutsideClick.set(true);
    this._disableOverlayClick.set(false);
    this._loading.set(false);
  }

  /**
   * Check if drawer has specific content type
   */
  hasContentType(type: DrawerContentType): boolean {
    return this.content()?.type === type;
  }

  /**
   * Check if drawer is in loading state
   */
  isLoading(): boolean {
    return this.loading();
  }

  /**
   * Get current drawer state as an object
   */
  getState() {
    return {
      isOpen: this.isOpen(),
      content: this.content(),
      loading: this.loading(),
      title: this.title(),
      subtitle: this.subtitle(),
      width: this.width(),
      position: this.position(),
      config: this.config(),
      context: this.context(),
    };
  }


  async openLoadedQuoteRequest(quoteRequest: any, options?: {
    title?: string;
    subtitle?: string;
    mode?: DrawerMode;
  }): Promise<void> {
    const content: DrawerContent = {
      type: 'loaded-quote-request',
      data: quoteRequest,
      config: {
        title: options?.title || 'List',
        subtitle: options?.subtitle,
        context: quoteRequest,
      },
    };

    await this.open(content, options);
  }


  /**
   * Handle drawer opened event
   */
  onDrawerOpened(): void {
    this._isOpen.set(true);
  }

  /**
   * Handle drawer closed event
   */
  onDrawerClosed(): void {
    this._isOpen.set(false);
    // Clear content after closing
    setTimeout(() => {
      this._content.set(null);
      this._config.set(null);
      this._context.set(null);
      this._title.set('');
      this._subtitle.set('');
      // this.resetToDefaults();
    }, 100);
  }

}
